import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute, Params } from '@angular/router'
import { ConsentUtil } from '../../utils'
import { historyBack } from '../../utils'
import { Consent, Customer, CustomerConsent } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { ConsentService } from '../../services/entity-services/consent.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const consentSchema = {
  id: null,
  name: null,
  externalId: null,
  body: null,
  created: null,
  updated: null,
}

@Component({
  selector: 'app-consent',
  templateUrl: './consent.component.html',
  styleUrls: ['./consent.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConsentComponent implements CheckDataChanges {
  private consentService = inject(ConsentService)
  private queryClient = injectQueryClient()

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  consentForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    externalId: new FormControl({ value: '', disabled: false }, []),
    body: new FormControl({ value: '', disabled: false }, []),
  })

  readonly queryConsent = injectQuery(() => ({
    queryKey: ['consent', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.consentService.getConsent(id, consentSchema)
      } else {
        return new Consent()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.consent-id')
      this.fillFormValue(data)
    },
  }))

  createConsent = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.consentForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return await this.consentService.createConsent(n, consentSchema) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.consentForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.consentForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.consent.created'))
      // Update detail cache for this consent
      this.queryClient.setQueryData(['consent', Number(data.id)], data)
      // Update all cached list queries beginning with ['consents', ...] so listings reflect the change immediately
      updateCachedListQueries(this.queryClient, ['consents'], data, { mode: 'insert', insertPosition: 'start' })

      this.removeConsentListings()

      this.router.navigate(['/consent/', data['id']], { replaceUrl: true })
    },
  }))

  updateConsent = injectMutation(() => ({
    mutationFn: () => {
      if (this.consentForm.invalid) {
        throw new Error('Form is invalid')
      }
      const consentDataToSave = this.getDataToSave()
      return this.consentService.updateConsent(consentDataToSave, consentSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.consentForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.consentForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.consent.updated'))
      // Update detail cache for this consent
      this.queryClient.setQueryData(['consent', Number(data.id)], data)
      // Update all cached list queries beginning with ['consents', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['consents'], data)

      this.removeConsentListings()

      this.router.navigate(['/consent', data['id']])
    },
  }))

  deleteConsent = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (!this.queryConsent.data()?.id) {
        alert(this.translateService.instant('consent.error.contact-admin'))
        return
      }
      const confirmation = confirm(
        this.translateService.instant('vat.delete-confirm', { name: this.consentForm.value.name })
      )
      if (confirmation) {
        const id = Number(this.route.snapshot.params['id'])
        await this.consentService.deleteConsent({ id })
      } else {
        // todo
      }
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('alert.consent.deleted'))
      // Prune deleted consent from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['consents', 'options'], { id: Number(idStr) } as any, {
        mode: 'delete',
      })
      this.removeConsentListings()
      this.router.navigate(['/consents'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return this.queryConsent.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const d = this.queryConsent.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.consentForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  goBack(): void {
    historyBack()
  }

  public fillFormValue(consent) {
    // const data = this.queryConsent.data() as any
    const value = {
      name: consent?.name ?? '',
      body: consent?.body ?? '',
      externalId: consent?.externalId ?? '',
    }
    this.consentForm.setValue(value)
    // Snapshot initial values and mark pristine for unsaved-changes detection
    this.initialFormValue.set(structuredClone(value))
    this.consentForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    // todo
    const current = this.consentForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: before snapshot is ready
    if (!initial) {
      return this.consentForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    return norm(current?.name) !== norm(initial?.name) || norm(current?.externalId) !== norm(initial?.externalId)
  }

  getDataToSave() {
    // todo
    const current = this.consentForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    // Optional fields — include only when changed
    if (norm(current.externalId) !== norm(initial?.externalId)) {
      patch.externalId = current.externalId
    }
    if (norm(current.body) !== norm(initial?.body)) {
      patch.body = current.body
    }

    return patch
  }

  printCustomerConsent(): void {
    if (!this.consentForm.value) {
      return
    }
    const customerConsent = new CustomerConsent({
      customer: new Customer(),
      consent: this.consentForm.value,
      name: this.consentForm.value.name,
      body: this.consentForm.value.body,
    })
    ConsentUtil.printCustomerConsent(customerConsent)
  }
  get formControls(): { [key: string]: AbstractControl } {
    return this.consentForm.controls
  }

  private removeConsentListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['consents'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }
}
